import { createFileRoute, redirect, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { AlertCircle, Building2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PrimaryButton } from '@/components/glitch-ui/primary-button';
import { approveOauthConsent, denyOauthConsent } from '@/lib/apis';
import { oauthConsentContextQueryOptions } from '@/lib/query-options';
import { isAuthenticated } from '@/lib/auth';
import { useUserProfile } from '@/contexts/user.context';
import type { OauthConsentParams } from '@/lib/types';
import logo from '/logo_lightbg.svg';

type ConsentSearch = OauthConsentParams;

export const Route = createFileRoute('/oauth/consent')({
  component: OauthConsentPage,
  validateSearch: (search: Record<string, unknown>): ConsentSearch => ({
    client_id: (search.client_id as string) ?? '',
    redirect_uri: (search.redirect_uri as string) ?? '',
    scope: (search.scope as string) ?? '',
    resource: (search.resource as string) ?? '',
    code_challenge: (search.code_challenge as string) ?? '',
    state: search.state as string | undefined,
  }),
  beforeLoad: ({ location }) => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/signin', search: { redirect: location.href } });
    }
  },
});

// The documented meaning of each scope, sentence case (D11).
const SCOPE_DESCRIPTIONS: Record<string, string> = {
  'organizations:read': 'View your organizations, their account overview and business profile',
  'campaigns:read': 'View campaigns and keywords, and run detectors',
  'campaigns:write': 'Edit campaign budgets, names and states, and create campaigns and ads',
  'keywords:write': 'Add negative keywords',
  'performance:read': 'View performance metrics',
  'recommendations:read': 'View optimization recommendations',
  'recommendations:apply': 'Apply optimizations and undo changes',
  'reports:read': 'View generated reports and Strategic Plans',
  'reports:generate': 'Generate new reports and write Strategic Plans',
  'audit:read': 'View the action log and export the audit trail',
  // Offered only to AdOps team members (#6796).
  'skills:run': "Run skills' collector tools, which inspect websites outside Glitch",
  'skills:read': "Read your team's and Glitch's skills and their versions",
  'skills:write': "Save new versions of your team's skills, never made live without a person",
};

function OauthConsentPage() {
  const params = useSearch({ from: '/oauth/consent' });
  const { data: context, isLoading, isError } = useQuery(oauthConsentContextQueryOptions(params));
  const [checkedScopes, setCheckedScopes] = useState<string[] | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Approving here would mint a connection acting as the customer, so the server refuses it (#6625).
  const { user } = useUserProfile();
  const isImpersonating = !!user?.impersonating_email;

  useEffect(() => {
    if (!context || checkedScopes !== null) return;
    setCheckedScopes(context.scopes);
  }, [context, checkedScopes]);

  const toggleScope = (scope: string) =>
    setCheckedScopes((current) =>
      (current ?? []).includes(scope) ? (current ?? []).filter((s) => s !== scope) : [...(current ?? []), scope],
    );

  const approveMutation = useMutation({
    mutationFn: () => approveOauthConsent({ ...params, approved_scopes: checkedScopes ?? [] }),
    onSuccess: ({ redirect_to }) => {
      window.location.href = redirect_to;
    },
    onError: () => setActionError('The approval could not be completed. Close this window and try again.'),
  });

  const denyMutation = useMutation({
    mutationFn: () =>
      denyOauthConsent({ client_id: params.client_id, redirect_uri: params.redirect_uri, state: params.state }),
    onSuccess: ({ redirect_to }) => {
      window.location.href = redirect_to;
    },
    onError: () => setActionError('The denial could not be delivered. Close this window and try again.'),
  });

  const isSubmitting = approveMutation.isPending || denyMutation.isPending;
  const newScopes = new Set(
    context?.existing_grant ? context.scopes.filter((scope) => !context.existing_grant?.scopes.includes(scope)) : [],
  );

  if (isLoading) {
    return (
      <ConsentShell>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
        </div>
      </ConsentShell>
    );
  }

  if (isError || !context) {
    return (
      <ConsentShell>
        <CardContent className="flex flex-col items-center gap-3 px-8 py-12 text-center">
          <AlertCircle className="h-10 w-10" style={{ color: '#B91C1C' }} />
          <p className="text-lg font-semibold text-gray-900">Authorization Request Invalid</p>
          <p className="text-sm text-gray-500">
            This authorization request is invalid or has expired. Return to the application and start again.
          </p>
        </CardContent>
      </ConsentShell>
    );
  }

  return (
    <ConsentShell>
      <CardHeader className="space-y-1.5 px-8 pb-4 pt-8">
        <CardTitle className="text-xl text-gray-900">
          {context.client.client_name} Wants Access to GlitchAds
        </CardTitle>
        <p className="text-sm text-gray-500">
          {context.existing_grant
            ? 'Access was granted before. Approving updates the existing connection.'
            : 'Access covers every organization on this account, capped by the role held in each.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-6 px-8 pb-8 pt-2">
        <div>
          <p className="mb-2 text-xs font-medium uppercase text-gray-500">Organizations Covered</p>
          <div className="divide-y rounded-lg border">
            {context.organizations.map((org) => (
              <div key={org.id} className="flex items-center gap-3 p-3">
                <Building2 className="h-4 w-4 shrink-0 text-gray-500" />
                <span className="flex-1 text-sm text-gray-900">{org.name}</span>
                <span className="text-xs text-gray-500">
                  {org.allowed ? org.role.toLowerCase() : 'Some permissions exceed the role held here'}
                </span>
              </div>
            ))}
          </div>
          {context.organizations.length === 0 && (
            <p className="text-sm text-gray-500">No organizations are available for this account.</p>
          )}
        </div>

        <div>
          <p className="mb-2 text-xs font-medium uppercase text-gray-500">Permissions</p>
          <div className="divide-y rounded-lg border">
            {context.scopes.map((scope) => {
              const isChecked = (checkedScopes ?? []).includes(scope);
              return (
                <label key={scope} className="flex cursor-pointer items-center gap-3 p-3 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isSubmitting}
                    onChange={() => toggleScope(scope)}
                    className="accent-[#290BD9]"
                  />
                  <span className="flex-1 text-sm text-gray-700">{SCOPE_DESCRIPTIONS[scope] ?? scope}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    {newScopes.has(scope) && (
                      <span
                        className="rounded-md px-2 py-0.5 text-xs font-medium"
                        style={{ color: '#290BD9', backgroundColor: '#EDE9FE' }}
                      >
                        New
                      </span>
                    )}
                    <span className="rounded-md bg-blue-100 px-2 py-1 font-mono text-xs text-blue-800">{scope}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {actionError && (
          <div
            className="flex items-center gap-2 border-l-4 p-3 text-sm"
            style={{ color: '#B91C1C', backgroundColor: '#FEE2E2', borderColor: '#FCA5A5' }}
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            {actionError}
          </div>
        )}

        {isImpersonating && (
          <div
            className="flex items-start gap-2 rounded-md border-l-4 p-3 text-sm"
            style={{ color: '#A16207', backgroundColor: '#FEF3C7', borderColor: '#FDE68A' }}
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            This session is impersonating a customer. A connection approved here would act as them long after the
            session ends, so only they can approve it.
          </div>
        )}

        <div className="space-y-3 pt-2">
          <PrimaryButton
            label="ALLOW ACCESS"
            size="lg"
            className="w-full py-6"
            disabled={isSubmitting || isImpersonating || (checkedScopes ?? []).length === 0}
            onClick={() => {
              setActionError(null);
              approveMutation.mutate();
            }}
          />
          <Button
            variant="outline"
            size="lg"
            className="w-full py-6 text-gray-900"
            disabled={isSubmitting}
            onClick={() => {
              setActionError(null);
              denyMutation.mutate();
            }}
          >
            DENY
          </Button>
        </div>
      </CardContent>
    </ConsentShell>
  );
}

function ConsentShell({ children }: { children: React.ReactNode }) {
  // The app sets body { overflow: hidden } globally and scrolls inside its main
  // content area, but this page renders outside that layout, so it must be its
  // own scroll container or anything below the fold is reachable only by Tab.
  // The card is wider than the app's dialogs on purpose: the consent request
  // lists every organization and up to ten scopes, and 480px wrapped the scope
  // descriptions against their code chips.
  return (
    <div className="h-screen overflow-y-auto">
      <div className="flex flex-col items-center py-16">
        <img src={logo} alt="logo" className="mb-8 h-[60px]" />
        <Card className="w-[560px] max-w-[calc(100vw-2rem)]">{children}</Card>
      </div>
    </div>
  );
}
